import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, model, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { filter, Observable, take, takeUntil } from 'rxjs';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';
import { ApiFragment } from '../../../core/records/entities/api-fragment.class';
import { ConfirmationDialogConfig, DialogHelperBase } from '../../../dynamic-dialog/helpers/dialog.helper';
import {
    ActionItem,
    ActionItemStyle,
    ShActionItemComponent
} from '../../../elements/components/action-item/action-item.component';
import { IconSize, ShIconModule } from '../../../elements/components/icon/icon.module';
import { ShLiveOptionsSelectComponent } from '../../../elements/components/live-options-select/live-options-select.component';
import { PaginatedResults } from '../../../overviews/generic-overview/components/pagination/entities/paginated-results.entity';
import { PaginationInfo } from '../../../overviews/generic-overview/components/pagination/entities/pagination-info.entity';
import { Face } from '../../entities/face.entity';
import { IsPersonPipe, isSetPerson, Person } from '../../entities/person.entity';
import { FaceInteractionService } from '../../services/face-interaction.service';
import { PersonDataService } from '../../services/person.data-service';
import { ShFaceTagComponent } from '../face-tag/face-tag.component';
import { FormGroupStyling } from '../../../form-controls/entities/form-group-styling.entity';

@Component({
    standalone: true,
    selector: 'sh-form-person-select',
    templateUrl: './form-person-select.component.html',
    styleUrls: ['./form-person-select.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ShFaceTagComponent,
        ShLiveOptionsSelectComponent,
        TranslateModule,
        NgTemplateOutlet,
        IsPersonPipe,
        ShIconModule,
        ShActionItemComponent
    ]
})
export class ShFormPersonSelectComponent extends BaseContainer {
    public readonly FormGroupStyling: typeof FormGroupStyling = FormGroupStyling;

    private readonly personDataService: PersonDataService = inject(PersonDataService);
    private readonly faceInteractionService: FaceInteractionService = inject(FaceInteractionService);
    private readonly dialogHelper: DialogHelperBase = inject(DialogHelperBase);

    // #region Inputs/Outputs
    public face = model.required<Face>();
    public $faceId = computed(() => this.face().Id);

    public id = input<string>();
    public label = input<string>();

    public faceChange = output<Face>();
    public faceDelete = output<string>();
    // #endregion

    // #region Create Person
    public createPerson(name: string): void {
        this.updatePersonValue({
            Name: name
        });

        this.unfocusFaceFrame();
    }

    // #endregion

    // #region Change Person
    public linkPerson(people: ApiFragment[]): void {
        if (people.length === 0 || people.length > 1) {
            return;
        }

        this.updatePersonValue({
            AiLinked: false,
            Name: people[0].getTitle(),
            RecordId: people[0].getRecordId()
        });

        this.unfocusFaceFrame();
    }
    // #endregion

    // #region Change Person name
    public changePersonName(newName: string): void {
        const currentPerson: Person | undefined = this.face().Person;
        if (!newName) {
            return;
        }

        if (isSetPerson(currentPerson)) {
            this.updatePersonValue({
                AiLinked: false,
                Name: newName,
                RecordId: currentPerson?.RecordId,
                NameChanged: true
            });
        } else {
            // Update name of new person
            this.updatePersonValue({
                Name: newName
            });
        }

        this.unfocusFaceFrame();
    }
    // #endregion

    // #region Remove Person
    public unlinkPerson(): void {
        this.updatePersonValue({
            AiLinked: false,
            RecordId: undefined
        });

        this.unfocusFaceFrame();
    }
    // #endregion

    // #region Delete face
    get deleteActionItem(): ActionItem {
        return {
            label: this.translateService.translate('facial-recognition.delete-face.title'),
            icon: 'trash',
            iconSize: IconSize.REGULAR,
            style: ActionItemStyle.PureIconButton,
            eventListener: this.deleteFace.bind(this)
        };
    }

    private deleteFace(): void {
        this.openDeleteWarningDialog()
            .pipe(
                take(1),
                takeUntil(this.destroy$),
                filter(confirmed => confirmed === true)
            )
            .subscribe(confirmed => this.onFaceDelete());
    }

    private openDeleteWarningDialog(): Observable<boolean> {
        const config: ConfirmationDialogConfig = {
            title: this.translateService.translate('facial-recognition.delete-face.title'),
            message: this.translateService.translate('facial-recognition.delete-face.message'),
            yes: this.translateService.translate('ui.remove'),
            no: this.translateService.translate('ui.cancel')
        };
        return this.dialogHelper.confirm(config);
    }

    private onFaceDelete(): void {
        this.faceDelete.emit(this.face().Id);
    }
    // #endregion

    // #region Interactions
    public focusFaceFrame() {
        this.faceInteractionService.setFaceFrameFocus(this.$faceId());
    }

    public unfocusFaceFrame() {
        this.faceInteractionService.setFaceFrameFocus(null);
    }

    public hoverFaceFrame() {
        this.faceInteractionService.setFaceFrameHover(this.$faceId());
    }

    public unhoverFaceFrame() {
        this.faceInteractionService.setFaceFrameHover(null);
    }
    // #endregion

    // #region Live Select helper methods
    public valueToLabelFct = (person: ApiFragment): string => {
        return person.getTitle();
    };

    public paginatedResultsFct = (
        searchTerm: string,
        paginationInfo: PaginationInfo
    ): Observable<PaginatedResults<ApiFragment>> => {
        return this.personDataService.getPersons(searchTerm, paginationInfo);
    };
    // #endregion

    // #region Utils
    private updatePersonValue(person: Person): void {
        const updatedFace = {
            ...this.face(),
            Person: person
        };
        this.face.set(updatedFace);
        this.faceChange.emit(updatedFace);
    }
    // #endregion
}
